import { useEffect, useRef, useState } from 'react';

type StaxJsTokenizePayload = Record<string, unknown>;

type StaxJsTokenizeResult = {
  id: string;
  customer_id?: string;
  customer?: { id?: string };
  [key: string]: unknown;
};

interface StaxJsInstance {
  showCardForm?: () => Promise<void> | void;
  tokenize: (payload: StaxJsTokenizePayload) => Promise<StaxJsTokenizeResult>;
}

type StaxJsConstructor = new (webPaymentsToken: string, config: unknown) => StaxJsInstance;

declare global {
  interface Window {
    StaxJs?: StaxJsConstructor;
    // Legacy name used in older code paths (e.g. ACH). Not provided by staxjs-captcha.js.
    Stax?: unknown;
  }
}

export type StaxTokenizeData = {
  // Expiration is required for tokenization.
  // Prefer passing month/year, but exp_date (MMYY or MM/YYYY) is accepted and normalized.
  month?: string;
  year?: string;
  exp_date?: string;

  firstname?: string;
  lastname?: string;
  person_name?: string;
  phone?: string;
  email?: string;
  address_1?: string;
  address_2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
  company?: string;
  validate?: boolean;
  customer_id?: string;
  // Allow additional Stax tokenization fields (e.g. match_customer) without fighting TS.
  [key: string]: unknown;
};

// Legacy callers (MembershipEnrollmentFlow) pass { form, extraDetails } so tokenization
// can read exp month/year from the form.
export type StaxTokenizeFormOptions = {
  form: HTMLFormElement;
  extraDetails: StaxTokenizeData;
};

export type StaxTokenizeArgs = StaxTokenizeData | StaxTokenizeFormOptions;

function isFormOptions(arg: StaxTokenizeArgs): arg is StaxTokenizeFormOptions {
  return Boolean((arg as StaxTokenizeFormOptions).form);
}

export type StaxTokenizeResult = {
  id: string;
  customer_id?: string;
  customer?: { id?: string };
  [key: string]: unknown;
};

export type StaxCardFormStatus = 'idle' | 'loading' | 'mounted' | 'error';

// Stax official hosted-fields script.
// NOTE: staxjs-captcha defines window.StaxJs (NOT window.Stax).
const STAXJS_SCRIPT_SRC = 'https://staxjs.staxpayments.com/staxjs-captcha.js';
const STAXJS_SCRIPT_ID = 'staxjs-captcha-sdk';

function getFieldIds() {
  return { number: 'card-number', cvv: 'card-cvv' };
}

function getFieldContainers() {
  const ids = getFieldIds();
  const number = document.getElementById(ids.number);
  const cvv = document.getElementById(ids.cvv);
  if (!number || !cvv) return null;
  return { number, cvv };
}

function hasHostedIframes(containers: { number: HTMLElement; cvv: HTMLElement }) {
  return Boolean(containers.number.querySelector('iframe')) && Boolean(containers.cvv.querySelector('iframe'));
}

async function waitForHostedIframes(timeoutMs: number): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const containers = getFieldContainers();
    if (containers && hasHostedIframes(containers)) return true;
    await new Promise((r) => setTimeout(r, 50));
  }
  return false;
}

function buildCardFormConfig() {
  const ids = getFieldIds();
  const style =
    'width: 100%; height: 100%; border: none; background-color: transparent; margin: 0; padding: 4px 0; ' +
    'font-size: 16px; line-height: 24px; font-weight: 400; font-family: ui-sans-serif, system-ui, -apple-system, ' +
    'BlinkMacSystemFont, "Segoe UI", sans-serif; color: #374151;';

  return {
    number: { id: ids.number, placeholder: '0000 0000 0000 0000', style },
    cvv: { id: ids.cvv, placeholder: '123', style },
  };
}

// NOTE: This app is multi-tenant. Each brand/company has its own Stax Web Payments Token.
export function resolveStaxWebPaymentsToken(hostname: string): string | undefined {
  const host = hostname.toLowerCase();

  if (host.includes('hales')) return import.meta.env.VITE_STAX_HALES_WEB_PAYMENTS_TOKEN;
  if (host.includes('abraham')) return import.meta.env.VITE_STAX_ABRAHAM_WEB_PAYMENTS_TOKEN;

  if (host.includes('my-ccs') || host.includes('climate')) {
    return import.meta.env.VITE_STAX_CLIMATE_WEB_PAYMENTS_TOKEN;
  }

  // Local/dev default
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return import.meta.env.VITE_STAX_CLIMATE_WEB_PAYMENTS_TOKEN;
  }

  // As a last resort, pick the first configured token.
  return (
    import.meta.env.VITE_STAX_CLIMATE_WEB_PAYMENTS_TOKEN ||
    import.meta.env.VITE_STAX_HALES_WEB_PAYMENTS_TOKEN ||
    import.meta.env.VITE_STAX_ABRAHAM_WEB_PAYMENTS_TOKEN
  );
}

async function ensureStaxJsScriptLoaded(): Promise<void> {
  if (window.StaxJs) return;

  const existing = document.getElementById(STAXJS_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      const onLoad = () => {
        existing.removeEventListener('load', onLoad);
        existing.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        existing.removeEventListener('load', onLoad);
        existing.removeEventListener('error', onError);
        reject(new Error('Failed to load StaxJs SDK.'));
      };

      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = STAXJS_SCRIPT_ID;
    script.src = STAXJS_SCRIPT_SRC;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load StaxJs SDK.'));

    document.body.appendChild(script);
  });
}

function normalizeExpirationFromForm(form: HTMLFormElement): { month: string; year: string; card_exp: string } {
  const monthInput = form.elements.namedItem('month') as HTMLInputElement | HTMLSelectElement | null;
  const yearInput = form.elements.namedItem('year') as HTMLInputElement | HTMLSelectElement | null;

  let month = String(monthInput?.value || '').trim();
  let year = String(yearInput?.value || '').trim();

  if (!month || !year) throw new Error('Please enter the card expiration date.');

  if (month.length === 1) month = `0${month}`;
  const monthNum = Number.parseInt(month, 10);
  if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    throw new Error('Please enter a valid expiration month (01-12).');
  }

  if (year.length === 2) year = `20${year}`;
  if (year.length !== 4) throw new Error('Please enter a 4-digit expiration year (e.g., 2027).');

  const card_exp = `${month}/${year.slice(-2)}`;
  return { month, year, card_exp };
}

function normalizeExpirationFromData(data: StaxTokenizeData): { month: string; year: string; card_exp: string } {
  if (data.month && data.year) {
    const fakeForm = {
      elements: {
        namedItem: (name: string) => {
          if (name === 'month') return { value: data.month };
          if (name === 'year') return { value: data.year };
          return null;
        },
      },
    } as unknown as HTMLFormElement;

    return normalizeExpirationFromForm(fakeForm);
  }

  const exp = String(data.exp_date || '').trim();
  if (!exp) throw new Error('Please enter the card expiration date.');

  // Accept MMYY or MM/YYYY
  const cleaned = exp.replace(/\s+/g, '');
  const mm = cleaned.slice(0, 2);
  const rest = cleaned.slice(2).replace(/^\//, '');
  const yyOrYyyy = rest;

  const monthNum = Number.parseInt(mm, 10);
  if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    throw new Error('Please enter a valid expiration month (01-12).');
  }

  let year = yyOrYyyy;
  if (year.length === 2) year = `20${year}`;
  if (year.length !== 4) throw new Error('Please enter a 4-digit expiration year (e.g., 2027).');

  const month = mm;
  const card_exp = `${month}/${year.slice(-2)}`;
  return { month, year, card_exp };
}

export function useStax() {
  const [isStaxReady, setIsStaxReady] = useState(false);
  const [staxError, setStaxError] = useState<string | null>(null);
  const [cardFormStatus, setCardFormStatus] = useState<StaxCardFormStatus>('idle');

  const staxRef = useRef<StaxJsInstance | null>(null);
  const showCardFormAttemptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        await ensureStaxJsScriptLoaded();
        if (!cancelled) setIsStaxReady(true);
      } catch (err: unknown) {
        console.error('StaxJs SDK load error:', err);
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load payment SDK.';
          setStaxError(message);
          setCardFormStatus('error');
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isStaxReady || staxError) return;

    const containers = getFieldContainers();
    if (!containers) {
      // Not on a payment step yet.
      return;
    }

    if (hasHostedIframes(containers)) {
      if (cardFormStatus !== 'mounted') setCardFormStatus('mounted');
      return;
    }

    const token = resolveStaxWebPaymentsToken(window.location.hostname);
    if (!token) {
      setStaxError('Stax is not configured for this domain (missing Web Payments Token).');
      setCardFormStatus('error');
      return;
    }

    if (!window.StaxJs) {
      setStaxError('StaxJs SDK loaded but did not initialize correctly.');
      setCardFormStatus('error');
      return;
    }

    if (!staxRef.current) {
      try {
        staxRef.current = new window.StaxJs(token, buildCardFormConfig());
      } catch (err: unknown) {
        console.error('StaxJs init error:', err);
        setStaxError('Payment form failed to initialize. Please refresh and try again.');
        setCardFormStatus('error');
        return;
      }
    }

    if (!showCardFormAttemptedRef.current && typeof staxRef.current.showCardForm === 'function') {
      showCardFormAttemptedRef.current = true;
      if (cardFormStatus !== 'loading') setCardFormStatus('loading');

      (async () => {
        try {
          await Promise.resolve(staxRef.current?.showCardForm?.());
          const ok = await waitForHostedIframes(2500);
          if (ok) {
            setCardFormStatus('mounted');
            return;
          }

          setCardFormStatus('error');
          setStaxError(
            'Secure card fields failed to load. Please disable blockers (ad/script blockers) and refresh the page.'
          );
        } catch (err: unknown) {
          console.error('StaxJs showCardForm error:', err);
          setCardFormStatus('error');
          setStaxError('Payment form failed to initialize. Please refresh and try again.');
        }
      })();
    }
  }, [isStaxReady, staxError, cardFormStatus]);

  const retryCardForm = () => {
    const containers = getFieldContainers();
    if (containers) {
      containers.number.innerHTML = '';
      containers.cvv.innerHTML = '';
    }

    staxRef.current = null;
    showCardFormAttemptedRef.current = false;
    setStaxError(null);
    setCardFormStatus('idle');
  };

  const tokenizePaymentMethodDetailed = async (data: StaxTokenizeArgs): Promise<StaxTokenizeResult> => {
    if (staxError) throw new Error(staxError);
    if (!isStaxReady) throw new Error('Payment system is still loading. Please try again in a moment.');

    const token = resolveStaxWebPaymentsToken(window.location.hostname);
    if (!token) throw new Error('Stax is not configured for this domain (missing Web Payments Token).');

    if (!window.StaxJs) throw new Error('StaxJs SDK is not available.');

    // Ensure secure fields exist
    const containers = getFieldContainers();
    if (!containers) throw new Error('Payment form is not ready. Please refresh the page and try again.');
    if (!hasHostedIframes(containers)) {
      throw new Error('Secure card fields are not ready yet. Please wait a moment, or refresh the page if the fields never appear.');
    }

    if (!staxRef.current) {
      staxRef.current = new window.StaxJs(token, buildCardFormConfig());
      if (typeof staxRef.current.showCardForm === 'function') {
        await Promise.resolve(staxRef.current.showCardForm());
        showCardFormAttemptedRef.current = true;
      }
    }

    let month: string;
    let year: string;
    let card_exp: string;
    let extraDetails: StaxTokenizeData;

    if (isFormOptions(data)) {
      ({ month, year, card_exp } = normalizeExpirationFromForm(data.form));
      extraDetails = data.extraDetails;
    } else {
      ({ month, year, card_exp } = normalizeExpirationFromData(data));
      extraDetails = data;
    }

    // Enforce hosted fields: do not accept raw PAN/CVV.
    if ('number' in extraDetails || 'cvv' in extraDetails) {
      throw new Error('Raw card details are not supported. Please use the secure card form fields.');
    }

    const {
      validate,
      firstname,
      lastname,
      person_name,
      address_2,
      ...rest
    } = extraDetails;

    const trimmedFirst = typeof firstname === 'string' ? firstname.trim() : '';
    const trimmedLast = typeof lastname === 'string' ? lastname.trim() : '';
    const resolvedPersonName =
      typeof person_name === 'string' && person_name.trim()
        ? person_name.trim()
        : trimmedFirst && trimmedLast
          ? `${trimmedFirst} ${trimmedLast}`.trim()
          : undefined;

    const payload: StaxJsTokenizePayload = {
      ...rest,
      address_2: typeof address_2 === 'string' ? address_2 : '',
      method: 'card',
      validate: typeof validate === 'boolean' ? validate : false,
      month,
      year,
      card_exp,
      ...(trimmedFirst ? { firstname: trimmedFirst } : {}),
      ...(trimmedLast ? { lastname: trimmedLast } : {}),
      ...(resolvedPersonName ? { person_name: resolvedPersonName } : {}),
    };

    const result = (await staxRef.current.tokenize(payload)) as StaxTokenizeResult;
    if (!result?.id) throw new Error('Stax tokenization succeeded but did not return a token id.');
    return result;
  };

  const tokenizePaymentMethod = async (data: StaxTokenizeArgs): Promise<string> => {
    const result = await tokenizePaymentMethodDetailed(data);
    return result.id;
  };

  return {
    isStaxReady,
    staxError,
    cardFormStatus,
    retryCardForm,
    tokenizePaymentMethod,
    tokenizePaymentMethodDetailed,
  };
}
